/**
 * Playthrough state machine for the runtime. Drives the runtime's session:
 * current scene, variable state, history snapshots for the Back button, and
 * the session event log (Phase 4d).
 *
 * Pure reducer. Side effects (console logging, future HTTP sync to a session
 * service) happen in a subscriber that watches the events array — the reducer
 * itself just appends events deterministically. This means every transition
 * is replayable from the action log alone.
 *
 * Eval order on `advance`, matching the V4 contract:
 *   1. Apply choice.effects → stateAfterEffects
 *   2. Select feedback against stateAfterEffects (recorded for display)
 *   3. Resolve destination against stateAfterEffects
 *   4. Apply destination's entryEffects → stateOnEntry
 *   5. Land on the destination node
 *
 * Variants are selected lazily by the renderer when displaying a scene; for
 * event emission the reducer selects them eagerly so the event log records
 * which variant fired.
 *
 * Events emitted per action:
 *   - `start` / `restart` → session_start + scene_entered (for the start node)
 *   - `advance` → choice_made + (scene_entered if dest exists)
 *                 + (session_completed if dest is an outcome)
 *   - `back` → no events. Back is a UI rewind; if we ever want analytics
 *              on back-button use, that's a separate event type.
 */
import {
  applyEffects,
  resolveDestination,
  selectFeedback,
  selectVariant,
  initialStateForScenario,
  type Choice,
  type Scenario,
  type SessionEvent,
  type State,
} from '@branchworks/shared';

/** A single step the learner took. */
export interface HistoryEntry {
  /** Scene we were on when we picked the choice. */
  nodeId: string;
  /** Variable state *before* the choice's effects ran (i.e. on entry to that scene). */
  stateBeforeEffects: State;
  /** The choice picked. */
  choiceId: string;
}

export interface PlaythroughState {
  currentNodeId: string;
  /** Variable state at the current scene, after all effects up to here. */
  state: State;
  /** Past steps, in order. The most recent step is `history[history.length - 1]`. */
  history: HistoryEntry[];
  /** The feedback text to show, if any. Cleared when transitioning. */
  pendingFeedback: string | null;
  /** Per-session unique id. New on start/restart; survives advance/back. */
  sessionId: string;
  /** Next sequence number to assign. 1-indexed. */
  sequenceNumber: number;
  /** Append-only event log for this session. */
  events: SessionEvent[];
  /** Whether session_completed has fired — prevents duplicate emission. */
  completed: boolean;
}

export type PlaythroughAction =
  | { type: 'start'; scenario: Scenario }
  | { type: 'advance'; scenario: Scenario; choice: Choice }
  | { type: 'back' }
  | { type: 'restart'; scenario: Scenario };

/** Generate a new session id. Short and human-readable. */
function newSessionId(): string {
  return 'sess_' + Math.random().toString(36).slice(2, 10);
}

/**
 * Compute the start-of-session state + initial events (session_start +
 * scene_entered for the start node). Used by both `start` and `restart`.
 */
function startSession(scenario: Scenario): PlaythroughState {
  const sessionId = newSessionId();
  const timestamp = new Date().toISOString();
  const initialState = initialStateForScenario(scenario);
  const startNode = scenario.nodes[scenario.startNodeId];

  const events: SessionEvent[] = [
    {
      type: 'session_start',
      sessionId,
      formatVersion: scenario.formatVersion,
      sequenceNumber: 1,
      timestamp,
      scenarioTitle: scenario.title,
      startNodeId: scenario.startNodeId,
      initialState,
    },
  ];

  let sequenceNumber = 2;

  if (startNode) {
    const variant = selectVariant(startNode, initialState);
    const variantIndex = Math.max(
      0,
      (startNode.variants || []).findIndex((v) => v.id === variant.id),
    );
    events.push({
      type: 'scene_entered',
      sessionId,
      formatVersion: scenario.formatVersion,
      sequenceNumber: sequenceNumber++,
      timestamp,
      nodeId: scenario.startNodeId,
      nodeTitle: startNode.title,
      state: initialState,
      variantIndex,
    });
  }

  return {
    currentNodeId: scenario.startNodeId,
    state: initialState,
    history: [],
    pendingFeedback: null,
    sessionId,
    sequenceNumber,
    events,
    completed: false,
  };
}

/**
 * The original init helper, retained for compatibility with `useReducer`'s
 * lazy-init API. It runs `startSession` so first render already has the
 * session_start + initial scene_entered events.
 */
export function initPlaythrough(scenario: Scenario): PlaythroughState {
  return startSession(scenario);
}

export function playthroughReducer(
  state: PlaythroughState,
  action: PlaythroughAction,
): PlaythroughState {
  switch (action.type) {
    case 'start':
    case 'restart':
      return startSession(action.scenario);

    case 'advance': {
      const { scenario, choice } = action;
      // 1. Apply the choice's effects.
      const stateAfterEffects = applyEffects(state.state, choice.effects);
      // 2. Select feedback (against stateAfterEffects). Empty text means "no
      // feedback shown" — recorded as null in the event regardless.
      const feedbackText = selectFeedback(choice, stateAfterEffects);
      // 3. Resolve destination.
      const destId = resolveDestination(choice, stateAfterEffects);

      const timestamp = new Date().toISOString();
      let sequenceNumber = state.sequenceNumber;
      const newEvents: SessionEvent[] = [];

      // Always emit choice_made — even if the destination is broken, the
      // learner did pick a choice and that's worth recording.
      newEvents.push({
        type: 'choice_made',
        sessionId: state.sessionId,
        formatVersion: scenario.formatVersion,
        sequenceNumber: sequenceNumber++,
        timestamp,
        fromNodeId: state.currentNodeId,
        choiceId: choice.id,
        choiceLabel: choice.label,
        stateBefore: state.state,
        stateAfter: stateAfterEffects,
        destinationId: destId || null,
        feedbackText: feedbackText || null,
      });

      const destNode = destId ? scenario.nodes[destId] : null;
      if (!destId || !destNode) {
        // Broken scenario — choice has no reachable destination. Keep the
        // learner on the current scene but record the choice + feedback. The
        // validator will have surfaced the issue already.
        return {
          ...state,
          pendingFeedback: feedbackText || null,
          sequenceNumber,
          events: [...state.events, ...newEvents],
        };
      }

      // 4. Apply destination's entry effects.
      const stateOnEntry = applyEffects(stateAfterEffects, destNode.entryEffects);

      // scene_entered for the destination
      const variant = selectVariant(destNode, stateOnEntry);
      const variantIndex = Math.max(
        0,
        (destNode.variants || []).findIndex((v) => v.id === variant.id),
      );
      newEvents.push({
        type: 'scene_entered',
        sessionId: state.sessionId,
        formatVersion: scenario.formatVersion,
        sequenceNumber: sequenceNumber++,
        timestamp,
        nodeId: destId,
        nodeTitle: destNode.title,
        state: stateOnEntry,
        variantIndex,
      });

      // session_completed if this is an outcome (and we haven't already
      // completed — re-entering an outcome via Back→advance shouldn't re-fire).
      let completed = state.completed;
      if (destNode.type === 'outcome' && !completed) {
        newEvents.push({
          type: 'session_completed',
          sessionId: state.sessionId,
          formatVersion: scenario.formatVersion,
          sequenceNumber: sequenceNumber++,
          timestamp,
          outcomeNodeId: destId,
          outcomeLabel: destNode.outcomeLabel || destNode.title,
          finalState: stateOnEntry,
          stepCount: state.history.length + 1,
        });
        completed = true;
      }

      // 5. Push history and land.
      return {
        currentNodeId: destId,
        state: stateOnEntry,
        history: [
          ...state.history,
          {
            nodeId: state.currentNodeId,
            stateBeforeEffects: state.state,
            choiceId: choice.id,
          },
        ],
        pendingFeedback: feedbackText || null,
        sessionId: state.sessionId,
        sequenceNumber,
        events: [...state.events, ...newEvents],
        completed,
      };
    }

    case 'back': {
      if (state.history.length === 0) return state;
      const previous = state.history[state.history.length - 1];
      // No event emission on back — it's a UI rewind, not a learner action.
      // Note: we deliberately do NOT trim the events log. The log is a
      // forward-only audit trail of what the learner *experienced*; they
      // did see the later scene before going back, so the events stay.
      return {
        ...state,
        currentNodeId: previous.nodeId,
        state: previous.stateBeforeEffects,
        history: state.history.slice(0, -1),
        pendingFeedback: null,
      };
    }

    default:
      return state;
  }
}
